import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Catch-all 404 route.
 *
 * Until this existed, an unknown route (typically caused by a *stale frontend
 * bundle* loaded from browser cache after a fresh deploy that added new
 * routes) would render NOTHING inside <Routes>, leaving only the topmenu
 * visible and creating the misleading impression of a "white page" crash.
 *
 * Now the user sees a clear message and a one-click hard-reload that
 * bypasses cache. This page is intentionally minimal — no API calls, no
 * conditional render — so it always succeeds in mounting even when other
 * code is broken.
 */
const NotFoundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Heuristic: routes added in newer deploys (e.g. /admin/brix-outliers)
  // most commonly hit this page only because the user is still on a cached
  // older bundle. We surface that hint explicitly.
  const looksLikeStaleBundle =
    location.pathname.startsWith('/admin/') ||
    location.pathname.startsWith('/brix') ||
    location.pathname.startsWith('/ingredients');

  useEffect(() => {
    document.title = 'Siden blev ikke fundet — SlushBook';
  }, []);

  const hardReload = () => {
    // Force a network-level refetch so a stale Service Worker / browser
    // cache can't serve the old HTML/JS bundle. Adding a cache-buster
    // query covers the rare case where the host honours `?` differently.
    const url = location.pathname + (location.search ? location.search + '&' : '?') + 'cb=' + Date.now();
    window.location.replace(url);
  };

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-4 py-12"
      data-testid="notfound-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-gray-100 p-6 sm:p-8 text-center">
        <div className="text-5xl mb-3">🔎</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="notfound-title">
          Siden blev ikke fundet
        </h1>
        <p className="text-sm text-gray-600 mb-4">
          Vi kunne ikke finde noget på <code className="text-xs">{location.pathname}</code>.
        </p>

        {looksLikeStaleBundle && (
          <div
            className="text-left bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900"
            data-testid="notfound-stale-bundle-hint"
          >
            <strong>Sandsynlig årsag:</strong> Din browser har cachet en ældre
            version af SlushBook. Tryk på &quot;Genindlæs&quot; nedenfor — det henter
            den nyeste version direkte fra serveren.
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-2">
          <button
            type="button"
            onClick={hardReload}
            data-testid="notfound-hard-reload"
            className="px-4 py-2 bg-[#115DA8] text-white rounded-lg font-medium hover:bg-[#0d4d8f] transition-colors"
          >
            Genindlæs (cache-bypass)
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="notfound-back-home"
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Tilbage til forsiden
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
